import clsx from 'clsx';
import Container from '@/components/layout/Container';
import ImageReference from '@/components/elements/ImageReference';
import ExternalLink from '@/components/elements/ExternalLink';

/**
 * A section that contains a title, content, and an image with a link, with the
 * image on the left or right side depending on the image_first parameter.
 * Similar to VideoWayfinder, but with an image instead of a video.
 */
const FeatureWayfinder = ({
  block,
  background_color,
  block_color,
  position
}) => {
  const {
    light_text,
    highlight,
    title,
    image,
    image_first,
    content,
    reference,
    external_link_url,
    link_label,
    _metadata
  } = block.feature;
  const { uid } = _metadata;

  const linkReference = reference?.[0];

  return (
    <section
      id={`${uid}_${position}`}
      data-name="wayfinder-feature"
      className={clsx(
        `relative py-10 sm:pt-32 sm:pb-15 lg:pt-15`,
        background_color ? `bg-[${background_color}]` : 'bg-white'
      )}
    >
      <Container fullpage={false}>
        <div className="relative">
          <div
            className={clsx(
              `relative m-auto flex w-full justify-center sm:absolute sm:-top-28 lg:top-0 lg:bottom-0 lg:w-max`,
              image_first ? 'lg:left-0' : 'lg:right-0'
            )}
          >
            <div className="mx-auto my-auto max-h-3/4 w-full max-w-xl object-cover sm:mb-4 lg:mb-auto">
              <ImageReference uid={image.uid} parent_uid={uid} />
            </div>
          </div>
          <div
            className={clsx(`-mt-4 sm:mt-0 lg:flex lg:flex-row`, {
              'lg:flex-row-reverse': image_first
            })}
          >
            <div className={`lg:max-w-[65%]`}>
              <div
                className={clsx(
                  `px-4 py-8 sm:flex sm:min-h-[500px] sm:items-center sm:justify-center sm:pt-72 lg:p-28`,
                  block_color ? `bg-[${block_color}]` : 'bg-warm-pewter-20'
                )}
              >
                <div
                  className={clsx(
                    `mx-auto text-center sm:mt-8 sm:max-w-[65%] lg:max-w-full lg:text-left`,
                    image_first
                      ? 'lg:mr-2 lg:ml-[160px] 2xl:ml-28'
                      : 'lg:mr-[160px] lg:ml-0 2xl:mr-28'
                  )}
                >
                  {highlight !== 'Blank' && <Highlight text={highlight} />}
                  {title && (
                    <h2
                      className={`${light_text ? 'text-white' : 'text-black'} sm:h3 h5 mt-0 mb-4.5 sm:mt-1`}
                    >
                      {title}
                    </h2>
                  )}
                  <div
                    className={`${light_text ? 'text-white' : 'text-black'} body-r sm:body-l mb-8`}
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                  {linkReference || external_link_url ? (
                    <ExternalLink
                      linkStyling=" btn btn-primary inline-block"
                      internalLink={linkReference?.url}
                      externalLink={external_link_url}
                      linkText={link_label}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

/**
 * The preheading text that goes above the title in the feature block e.g. "NEW".
 */
const Highlight = ({ text }) => {
  return (
    <span className="preheading-s sm:preheading-r text-parlor-green mb-2 block sm:mt-2 lg:mb-0">
      {text}
    </span>
  );
};

export default FeatureWayfinder;
